import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationCancel, NavigationCancellationCode, NavigationEnd, NavigationError, PRIMARY_OUTLET, Router, UrlSegment, UrlSegmentGroup } from '@angular/router';
import * as Sentry from '@sentry/angular';

const RETRY_FLAG_KEY = 'tracit.chunkLoadRetryUrl';

/**
 * The router swallows failed navigations by default: on `NavigationError` (and
 * on guard-rejected `NavigationCancel`) it silently stays on the current page,
 * so to the user clicking a link just appears to do nothing. This service
 * turns those silent failures into a recovery or, at minimum, telemetry.
 *
 * Handled cases:
 *
 * 1. Stale lazy chunks. After a new deploy, a tab still running the old bundle
 *    gets a 404 when the router lazily loads a feature module chunk, because
 *    that chunk's hashed filename no longer exists. Recover by doing a full
 *    page load of the target URL, which fetches the current index.html/bundle.
 *    Guarded by sessionStorage so a genuinely broken chunk doesn't reload-loop.
 *
 * 2. Malformed auxiliary modal URLs. Angular parses
 *    `/o/123/staff(modal:add-agent)` with the `modal` outlet at the root,
 *    while this app's modal routes live under the dashboard feature route.
 *    Recover by normalizing those root-level modal outlets to the routable
 *    feature-scoped form, e.g. `/o/123/(staff//modal:add-agent)`.
 *
 * 3. Anything else lands in Sentry with the failed target URL so "links
 *    randomly do nothing" reports can be diagnosed.
 */
@Injectable({ providedIn: 'root' })
export class NavigationRecoveryService {
  private initialized = false;
  private readonly reportedFailures = new Set<string>();
  private readonly maxReportsPerSession = 20;

  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: object,
  ) { }

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    // Recovery re-navigations and failure telemetry only make sense in the
    // browser; during SSR/prerender a navigation failure surfaces through the
    // render output itself.
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.router.events.subscribe(event => {
      if (event instanceof NavigationError) {
        this.handleNavigationError(event);
      } else if (event instanceof NavigationCancel) {
        this.handleNavigationCancel(event);
      } else if (event instanceof NavigationEnd) {
        this.clearRetryFlag();
      }
    });
  }

  private handleNavigationError(event: NavigationError): void {
    if (this.isChunkLoadError(event.error)) {
      this.recoverFromChunkLoadError(event);
      return;
    }

    if (this.isRouteMatchError(event.error)) {
      this.recoverFromRouteMatchError(event);
      return;
    }

    this.reportNavigationFailure('Navigation error', event.url, event.error);
  }

  private handleNavigationCancel(event: NavigationCancel): void {
    // A guard returning `false` (rather than a redirect UrlTree) leaves the
    // user on the current page with no feedback. Nothing to recover, but
    // record it so dead links are visible in telemetry.
    if (event.code === NavigationCancellationCode.GuardRejected) {
      this.reportNavigationFailure('Navigation rejected by guard', event.url);
    }
  }

  private recoverFromChunkLoadError(event: NavigationError): void {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return;
    }

    const targetUrl = event.url || this.router.url;

    if (window.sessionStorage.getItem(RETRY_FLAG_KEY) === targetUrl) {
      // Already retried this exact navigation once; avoid a reload loop.
      this.reportNavigationFailure('Chunk load retry failed', targetUrl, event.error);
      return;
    }

    window.sessionStorage.setItem(RETRY_FLAG_KEY, targetUrl);
    window.location.href = targetUrl;
  }

  private recoverFromRouteMatchError(event: NavigationError): void {
    const normalizedModalUrl = this.normalizeRootAuxiliaryModalUrl(event.url);

    if (normalizedModalUrl !== null && normalizedModalUrl !== event.url) {
      this.reportNavigationFailure('Navigation recovered from malformed modal URL', event.url, event.error);
      void this.router.navigateByUrl(normalizedModalUrl);
      return;
    }

    this.reportNavigationFailure('Navigation failed to match any route', event.url, event.error);
    void this.router.navigate(['/not-found'], { state: { attemptedUrl: event.url } });
  }

  /**
   * Angular's compact-looking `/o/123/staff(modal:add-agent)` syntax places
   * the `modal` outlet at the URL root. Our dashboard modal outlets are
   * children of `/o/:groupId`, `/a/:agentId`, or selected admin sub-features,
   * so direct/cold loads need the unambiguous scoped form.
   */
  private normalizeRootAuxiliaryModalUrl(url: string): string | null {
    try {
      const tree = this.router.parseUrl(url);
      const primary = tree.root.children[PRIMARY_OUTLET];
      const rootModal = tree.root.children['modal'];

      if (!primary || !rootModal) {
        return null;
      }

      const baseLength = this.dashboardModalBaseLength(primary.segments);
      if (baseLength === null || primary.segments.length < baseLength) {
        return null;
      }

      const rootAuxiliaryChildren = Object.entries(tree.root.children)
        .filter(([outlet]) => outlet !== PRIMARY_OUTLET);

      if (!rootAuxiliaryChildren.length) {
        return null;
      }

      for (const [outlet] of rootAuxiliaryChildren) {
        delete tree.root.children[outlet];
      }

      const baseSegments = primary.segments.slice(0, baseLength);
      const remainingPrimarySegments = primary.segments.slice(baseLength);
      const scopedChildren: Record<string, UrlSegmentGroup> = {
        ...primary.children,
      };

      if (remainingPrimarySegments.length > 0) {
        scopedChildren[PRIMARY_OUTLET] = new UrlSegmentGroup(
          remainingPrimarySegments,
          primary.children[PRIMARY_OUTLET]?.children ?? {},
        );
      }

      for (const [outlet, group] of rootAuxiliaryChildren) {
        scopedChildren[outlet] = group;
      }

      tree.root.children[PRIMARY_OUTLET] = new UrlSegmentGroup(baseSegments, scopedChildren);
      return this.router.serializeUrl(tree);
    } catch {
      return null;
    }
  }

  private dashboardModalBaseLength(segments: UrlSegment[]): number | null {
    const firstSegment = segments[0]?.path;
    const secondSegment = segments[1]?.path;

    if ((firstSegment === 'a' || firstSegment === 'o') && segments.length >= 2) {
      return 2;
    }

    if (firstSegment === 'admin') {
      return ['documents', 'document-activity', 'notes'].includes(secondSegment ?? '')
        ? 2
        : 1;
    }

    return null;
  }

  private reportNavigationFailure(message: string, url: string, error?: unknown): void {
    console.error(`[NavigationRecovery] ${message}`, url, error);

    // A guard that rejects every click (or a link that keeps failing) would
    // otherwise emit an unbounded stream of identical Sentry events.
    const reportKey = `${message}|${url}`;
    if (this.reportedFailures.has(reportKey) || this.reportedFailures.size >= this.maxReportsPerSession) {
      return;
    }
    this.reportedFailures.add(reportKey);

    Sentry.captureMessage(message, {
      level: 'warning',
      extra: {
        targetUrl: url,
        currentUrl: this.router.url,
        errorMessage: error instanceof Error ? error.message : error === undefined ? undefined : String(error),
      },
    });
  }

  private clearRetryFlag(): void {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.removeItem(RETRY_FLAG_KEY);
    }
  }

  private isChunkLoadError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error ?? '');
    return /loading chunk|chunkloaderror|failed to fetch dynamically imported module|importing a module script failed/i.test(message);
  }

  private isRouteMatchError(error: unknown): boolean {
    // In production builds Angular strips the "Cannot match any routes"
    // message (it is ngDevMode-only) and the error is a RuntimeError whose
    // code is 4002 (NO_MATCH) with message "NG04002: ...", so match on the
    // code first and only fall back to the dev-mode message text.
    if (typeof (error as { code?: unknown })?.code === 'number') {
      return (error as { code: number }).code === 4002;
    }

    const message = error instanceof Error ? error.message : String(error ?? '');
    return /NG04002|cannot match any routes/i.test(message);
  }
}
